import type React from "react";

import { useId, useMemo, CSSProperties } from "react";

import FormLabel from "./FormLabel";
import FieldError, { invalidProps } from "./FieldError";

const baseClass: string = "newprofile-form-input";

export interface InputBaseParamsTemplate
{
    id?: string;
    name?: string;

    style?: CSSProperties;
    classOverride?: string;
    classAppend?: string;

    value?: string | number | readonly string[] | null;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    pattern?: string;
    autoComplete?: React.HTMLInputAutoCompleteAttribute;
    inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];

    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;

    children: React.ReactNode;
    labelClassOverride?: string;
    labelClassAppend?: string;
    labelSuffix?: React.ReactNode;

    maxLength?: number;
    min?: string;
    max?: string;

    // Validation message;
    error?: string;

    //TODO: Disabled, etc
}

export interface InputBaseParams extends InputBaseParamsTemplate
{
    type: string;
}

export const buildClassName = (classOverride?: string, classAppend?: string): string | undefined =>
{
    if (!classOverride)
    {
        return classAppend;
    }

    if (!classAppend)
    {
        return classOverride;
    }

    return classOverride + " " + classAppend;
};

//Standardized text input
const InputBase: React.FC<InputBaseParams> =
    ({
        type,
        id,
        name,
        style,
        classOverride = baseClass,
        classAppend,
        value,
        placeholder,
        required = false,
        disabled = false,
        pattern,
        autoComplete,
        inputMode,
        onBlur,
        onChange,
        onFocus,
        children,
        labelClassOverride,
        labelClassAppend,
        labelSuffix,
        maxLength,
        min,
        max,
        error
    }) =>
    {
        const defaultId = useId();

        id = id ?? defaultId;

        if (value === null)
        {
            value = undefined;
        }

        const className: string | undefined = useMemo(() => buildClassName(classOverride, classAppend), [classOverride, classAppend]);

        const label = useMemo(
            () => (
                <FormLabel
                    htmlFor={id}
                    required={required}
                    classOverride={labelClassOverride}
                    classAppend={labelClassAppend}
                    suffix={labelSuffix}
                >
                    {children}
                </FormLabel>
            ),
            [id, required, labelClassOverride, labelClassAppend, labelSuffix, children]);

        return (
            <div>
                {label}
                <input
                    type={type}

                    id={id}
                    name={name}

                    style={style}
                    className={className}

                    value={value}
                    placeholder={placeholder}
                    required={required}
                    disabled={disabled}
                    pattern={pattern}
                    autoComplete={autoComplete}
                    inputMode={inputMode}

                    onBlur={onBlur}
                    onChange={onChange}
                    onFocus={onFocus}

                    maxLength={maxLength}
                    min={min}
                    max={max}

                    {...invalidProps(id, error)}
                />
                <FieldError controlId={id} message={error} />
            </div>
        );
    };

export default InputBase;